import useAiChatRepo from '@/composables/ai-chat/ai-chat-repo';
import { aiChatMessageRole } from 'shared/constants.json';
import { computed, ref } from 'vue';

const timestamp = (message) => {
  const parsed = Date.parse(message.createdAt);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// createdAt is rounded to the second, so a question and its answer can carry the same
// timestamp; uid rises with every write and settles the order the timestamp cannot.
const sortMessages = (messages) => [...messages]
  .sort((a, b) => timestamp(a) - timestamp(b) || a.uid - b.uid);

export default function useAiChatConversation(chatId, { onChatCreated = () => {} } = {}) {
  const { selectChat, createMessage } = useAiChatRepo();

  const sending = ref(false);
  // the question stands in for itself until the write comes back with it, so the conversation
  // shows what was asked before it shows that something is answering
  const outgoing = ref(null);

  const messages = computed(() => {
    if (chatId.value === null) {
      return [];
    }

    const chat = selectChat(chatId.value);
    if (chat === undefined) {
      return [];
    }

    return sortMessages(chat.messages ?? []);
  });

  // Derived so it survives a reload: the answer arrives on the vortex subscription, not on
  // a deadline of ours. `sending` covers only the gap before the question reaches the store.
  const pending = computed(() => sending.value
    || messages.value[messages.value.length - 1]?.role === aiChatMessageRole.user);

  const sendMessage = (content, attachments = []) => {
    const question = content.trim();
    if ((question === '' && attachments.length === 0) || pending.value) {
      return Promise.resolve();
    }

    sending.value = true;
    const chatEdge = chatId.value === null ? {} : { chat: { uid: chatId.value } };
    const attachmentsAs = (shape) => (attachments.length === 0
      ? {}
      : { attachments: attachments.map(shape) });

    // the write references what was stored; the echo stands in for the message until that
    // comes back, so it needs the shape a storageObject edge is read in
    const attachmentEdge = attachmentsAs(({ fileId }) => ({ blob: fileId }));
    const echoed = attachmentsAs(({ fileId, name, getURL }) => ({ uid: fileId, fileName: name, getURL }));

    outgoing.value = { role: aiChatMessageRole.user, content: question, ...echoed };

    // a message created without a chat gets one from the backend, and the response is
    // selected after every payload ran, so the chat it was attached to comes back with it
    return createMessage({ ...chatEdge, ...attachmentEdge, role: aiChatMessageRole.user, content: question })
      .finally(() => { outgoing.value = null; })
      .then((message) => {
        if (chatId.value === null) {
          onChatCreated(message.chat.uid);
        }
      })
      .finally(() => { sending.value = false; });
  };

  return {
    messages,
    outgoing,
    pending,
    sendMessage,
  };
}
